import { Component, type ReactNode } from "react";
import { addBreadcrumb, captureException } from "@sentry/react";

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	error?: Error;
}

// Unattended kiosk: nobody is there to click the buttons, so the boundary
// also reloads automatically after this delay to self-heal
const AUTO_RELOAD_DELAY_MS = 60_000;

// Componente para capturar erros e permitir recuperação do kiosk
export class ErrorBoundary extends Component<Props, State> {
	private autoReloadTimeout?: ReturnType<typeof setTimeout>;

	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error) {
		captureException(error);

		clearTimeout(this.autoReloadTimeout);
		this.autoReloadTimeout = setTimeout(() => {
			addBreadcrumb({
				category: "kiosk",
				message: "ErrorBoundary auto-reload after crash",
				level: "warning",
			});
			window.location.reload();
		}, AUTO_RELOAD_DELAY_MS);
	}

	componentWillUnmount() {
		clearTimeout(this.autoReloadTimeout);
	}

	handleReset = () => {
		localStorage.clear();
		window.location.href = "/";
	};

	handleReload = () => {
		clearTimeout(this.autoReloadTimeout);
		this.setState({ hasError: false, error: undefined });
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			return (
				<div
					style={{
						padding: 40,
						textAlign: "center",
						color: "white",
						background: "#1a1a1a",
						minHeight: "100vh",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						gap: 16,
					}}
				>
					<h1 style={{ fontSize: 24 }}>Erro ao carregar aplicação</h1>
					<p style={{ opacity: 0.7, maxWidth: 400 }}>
						Ocorreu um problema inesperado. Tente recarregar ou reiniciar a
						aplicação. Recarregando automaticamente em 1 minuto...
					</p>
					<div style={{ display: "flex", gap: 12, marginTop: 16 }}>
						<button
							type="button"
							onClick={this.handleReload}
							style={{
								padding: "12px 24px",
								fontSize: 16,
								cursor: "pointer",
								background: "#333",
								color: "white",
								border: "1px solid #555",
								borderRadius: 4,
							}}
						>
							Recarregar
						</button>
						<button
							type="button"
							onClick={this.handleReset}
							style={{
								padding: "12px 24px",
								fontSize: 16,
								cursor: "pointer",
								background: "#de2624",
								color: "white",
								border: "none",
								borderRadius: 4,
							}}
						>
							Reiniciar Aplicação
						</button>
					</div>
					{this.state.error && (
						<p
							style={{
								marginTop: 24,
								fontSize: 12,
								opacity: 0.4,
								fontFamily: "monospace",
							}}
						>
							{this.state.error.message}
						</p>
					)}
				</div>
			);
		}
		return this.props.children;
	}
}
